import { useMutation } from "@tanstack/react-query";
import { ButtonPrimary } from "components/Atoms/Button";
import { TextArea } from "components/Atoms/formInputs/TextArea";
import { Message } from "components/Atoms/Message";
import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import { FileDropzone } from "components/FileUpload/FileDropzone";
import { SidePanelContent, SidePanelHeader } from "components/SidePanel/SidePanel";
import { SidePanelActionsContext } from "components/SidePanel/SidePanelContext";
import SmallDetails from "components/SmallDetails/SmallDetails";
import React, { useContext, useState } from "react";
import { type FileRejection } from "react-dropzone";
import { Progress } from "semantic-ui-react";
import { formatBytes } from "shared/utils/fileutils";
import { uploadFilesToCoreApi } from "utilities/fileUploadUtils";

const maxSizeBytes = serverReference.constants.file.maximumClientFileContentLength;
export const advancingUploadMutationKey = ["AdvancingUpload.Mutation"];

interface Props {
  instruction: server.dto.AdvancingSessionInstructionBase
  onUploaded: () => void
}

export const UploadAdvancingFilesSidePanel = (props: Props) => {
  const { closePanel } = useContext(SidePanelActionsContext);
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string>();
  const [uploadPercentage, setUploadPercentage] = useState<number>();

  const uploadMutation = useMutation({
    mutationKey: advancingUploadMutationKey,
    mutationFn: (filesToUpload: File[]) => uploadFilesToCoreApi(
      window.CoreApi.AdvancingUpload.Create,
      window.CoreApi.AdvancingUpload.Create.absoluteUrl(),
      { ...props.instruction, message, files: filesToUpload },
      {
        fileType: "any",
        overrideMaxFileLength: maxSizeBytes,
        onUploadProgress: e => setUploadPercentage(Math.round((e.loaded * 100) / e.total))
      }
    ).then(r => r.data)
  });
  const isUploading = uploadMutation.isLoading;

  const onDrop = (dropped: FileList | File[]) => {
    setError(undefined);
    setFiles(current => [...current, ...Array.from(dropped)]);
  };

  const onDropReject = (fileRejections: FileRejection[]) => {
    setError(fileRejections[0].errors[0].message);
  };

  const upload = async () => {
    if (isUploading || files.length === 0) return;

    setError(undefined);
    setUploadPercentage(0);

    const response = await uploadMutation.mutateAsync(files);

    if (response.hasErrors) {
      setError(response.errors[0].message);
      setUploadPercentage(undefined);
      return;
    }

    props.onUploaded();
    closePanel();
  };

  return <>
    <SidePanelHeader title="Upload files" />
    <SidePanelContent>
      <div className="flex-column gap">
        <div className="body-text-regular s1_gray">{RESX.File.Files}</div>
        <FileDropzone
          iconType="file"
          maxSizeBytes={maxSizeBytes}
          onDrop={onDrop}
          onDropReject={onDropReject}
        />
        {files.map((file, index) => <div key={index} className="flex align-center justify-space-between gap">
          <SmallDetails label={file.name} subLabel={formatBytes(file.size)} />
          <PhosphorXButton layout="in-square" onClick={() => setFiles(current => current.filter((_, i) => i !== index))} />
        </div>)}
      </div>
      <div className="flex-column">
        <div className="body-text-regular s1_gray">{RESX.GeneralLabels.Comment}</div>
        <TextArea
          value={message}
          onChange={setMessage}
          rows={4}
          maxLength={serverReference.constants.advancingUpload.maxMessageLength}
        />
      </div>
      {isUploading && <Progress percent={uploadPercentage} color="green" progress label={<div style={{ color: "var(--text-color)" }}>{RESX.File.UploadingFiles}</div>} active />}
      {error && <Message type="error">{error}</Message>}
      <div className="flex justify-end">
        <ButtonPrimary label={RESX.Buttons.Upload} onClick={upload} loading={isUploading} disabled={files.length === 0} />
      </div>
    </SidePanelContent>
  </>;
};
